import React, { useState, useMemo } from "react";
import styled from "styled-components";
import { Trans, useTranslation } from "react-i18next";
import { useStripe } from "@stripe/react-stripe-js";
import { post, postApi } from "../../../api/getUser";
import { getDeclineErrorMessage } from "../../../shared/helpers";
import { formatNumber } from "../../../shared/helpers";
import {
  AddFormButton,
  FilterButton,
} from "../../../shared/components/TableElements";
import { LinkSpan, Desc } from "../../../shared/components/form/FormElements";
import {
  colorLightGray,
  colorTextAdditional,
  colorLightText,
} from "../../../utils/palette";
import CancelAddonModal from "./CancelAddonModal";
import ReactivateAddonModal from "./ReactivateAddonModal";
import ConfirmAddonModal from "./ConfirmAddonModal";
import { set } from "date-fns";
import { useAuth } from "../../../contexts/AuthContext";
import Wex from "../../Settings/Integrations/components/Wex";
import { PriceBox, Wrapper } from "./TelematicsAddonRow";

const STATUS = {
  LOCKED: "locked",
  ELIGIBLE: "inactive",
  ACTIVE: "active",
  CANCELLING: "canceled",
};

const WexAddonRow = ({
  status = STATUS.ELIGIBLE,
  isSubscriptionActive,
  isMobileSubscription,
  vehicleCount,
  unitPrice,
  currency,
  inputDuration,
  planName,
  totalVehicleSubAmount,
  periodEndDate,
  totalAfterCancellation,
  equipmentStatus,
  equipmentUnitPrice,
  equipmentQuantity,
  telematicsUnitPrice,
  telematicsStatus,
  isNotOldAdvancedUser,
  onAddSuccess,
  onCancelSuccess,
  setShowMobileAddOnErrPopup,
  isWexEligiblePlan,
  setShowWexPlanErrPopup,
  paymentData,
  currentProductId,
  onSubscribeClick,
}) => {
  const { t } = useTranslation("common");
  const stripe = useStripe();
  const { integrationsData, setIntegrationsData } = useAuth();

  const isWexSetupComplete = useMemo(
    () =>
      integrationsData?.some(
        (d) =>
          d.provider_type === 2 && d.provider === 13 && d.service_enabled === 1,
      ),
    [integrationsData],
  );

  const isWexSetupInProgress = useMemo(
    () =>
      integrationsData?.some(
        (d) =>
          d.provider_type === 2 && d.provider === 13 && d.service_enabled !== 1,
      ),
    [integrationsData],
  );

  const refreshIntegrationsData = async () => {
    const res = await post("integrations", {
      querystring: "get_integrations_data",
    });
    if (res.success && res.data) {
      setIntegrationsData(res.data);
    }
  };

  const [showWexPopup, setShowWexPopup] = useState(false);

  // const canToggle =
  //   status === STATUS.ELIGIBLE ||
  //   status === STATUS.ACTIVE
  //   ||
  //   status === STATUS.CANCELLING;
  // const [isCollapsed, setIsCollapsed] = useState(
  //   status === STATUS.ELIGIBLE || status === STATUS.ACTIVE,
  // );
  const canToggle =
    status !== STATUS.LOCKED && (isSubscriptionActive || isMobileSubscription);
  //const canToggle = (status !== STATUS.CANCELLING && isSubscriptionActive) || status === STATUS.LOCKED;

  const [isCollapsed, setIsCollapsed] = useState(status !== STATUS.LOCKED);

  const [loading, setLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [showAddConfirm, setShowAddConfirm] = useState(false);
  const [error, setError] = useState("");

  const billingLabel = inputDuration === 2 ? t("year_sm") : t("month_sm");
  const grandTotalLabel =
    inputDuration === 2 ? t("new_yearly_total") : t("new_monthly_total");
  const billingNote = paymentData
    ? t("confirm_changes_billing_note")
    : undefined;

  const addonTotal = useMemo(
    () => Number(unitPrice) * Number(vehicleCount),
    [unitPrice, vehicleCount],
  );

  const estimatedTotal = useMemo(() => {
    if (totalVehicleSubAmount == null) return null;
    const equipTotal =
      equipmentStatus === STATUS.ACTIVE
        ? (equipmentUnitPrice || 0) * (equipmentQuantity || 0)
        : 0;
    const telematicsTotal =
      telematicsStatus === STATUS.ACTIVE && isNotOldAdvancedUser
        ? (telematicsUnitPrice || 0) * (vehicleCount || 0)
        : 0;
    return (
      Number(totalVehicleSubAmount) + addonTotal + equipTotal + telematicsTotal
    );
  }, [
    addonTotal,
    totalVehicleSubAmount,
    equipmentStatus,
    equipmentUnitPrice,
    equipmentQuantity,
    telematicsStatus,
    telematicsUnitPrice,
    isNotOldAdvancedUser,
    vehicleCount,
  ]);

  const subTitleText =
    status === STATUS.ACTIVE
      ? `${vehicleCount} ${t("vehicles_sm")} × ${currency} ${formatNumber(unitPrice, 2)} ${t("per")} ${billingLabel}`
      : status === STATUS.CANCELLING
        ? `${t("cancels_on")} ${periodEndDate || ""}`
        : `${currency} ${formatNumber(unitPrice, 2)} ${t("per")} ${t("vehicle_sm")} ${t("per")} ${billingLabel}`;

  const handleAdd = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await postApi(
        { querystring: "add_fuel_card_addon" },
        "subscription",
      );
      if (res.success) {
        onAddSuccess();
      } else {
        if (res?.requires_action && res?.client_secret) {
          const confirmResult = await stripe.confirmCardPayment(res.client_secret);
          if (confirmResult.paymentIntent) {
            onAddSuccess();
            return;
          } else if (confirmResult.error) {
            setError(getDeclineErrorMessage("payment_intent_authentication_failure", t, "error_adding_wex"));
            return;
          } else {
            setError(getDeclineErrorMessage("payment_intent_authentication_failure", t, "error_adding_wex"));
            return;
          }
        }
        setError(
          getDeclineErrorMessage(res.decline_code, t, "error_adding_wex"),
        );
      }
    } catch {
      setError(t("error_adding_wex"));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await postApi(
        { querystring: "cancel_fuel_card_addon" },
        "subscription",
      );
      if (res.success) {
        setShowCancelConfirm(false);
        onCancelSuccess();
      } else {
        setError(t("error_cancelling_wex"));
      }
    } catch {
      setError(t("error_cancelling_wex"));
    } finally {
      setLoading(false);
    }
  };

  const handleReactivate = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await postApi(
        { querystring: "add_fuel_card_addon" },
        "subscription",
      );
      if (res.success) {
        setShowReactivateModal(false);
        onAddSuccess();
      } else {
        if (res?.requires_action && res?.client_secret) {
          const confirmResult = await stripe.confirmCardPayment(res.client_secret);
          if (confirmResult.paymentIntent) {
            setShowReactivateModal(false);
            onAddSuccess();
            return;
          } else if (confirmResult.error) {
            setError(getDeclineErrorMessage("payment_intent_authentication_failure", t, "error_reactivating_wex"));
            return;
          } else {
            setError(getDeclineErrorMessage("payment_intent_authentication_failure", t, "error_reactivating_wex"));
            return;
          }
        }
        setError(
          getDeclineErrorMessage(res.decline_code, t, "error_reactivating_wex"),
        );
      }
    } catch {
      setError(t("error_reactivating_wex"));
    } finally {
      setLoading(false);
    }
  };

  const featureList = [
    t("wex_feature_auto_import"),
    t("wex_feature_no_manual_entry"),
    t("wex_feature_realtime_visibility"),
  ];

  let plansLabel = t("vehicle_plan_plus_wex");
  if (telematicsStatus === STATUS.ACTIVE && isNotOldAdvancedUser) {
    plansLabel += `  + ${t("telematics")}`;
  }
  if (equipmentStatus === STATUS.ACTIVE) {
    plansLabel += `  + ${t("small_equipment")}`;
  }

  return (
    <Wrapper $status={status} role="region" aria-label={t("wex_fuel_card")}>
      <Header
        role={canToggle ? "button" : "heading"}
        aria-expanded={!isCollapsed}
        tabIndex={canToggle ? 0 : -1}
        onClick={() => canToggle && setIsCollapsed((prev) => !prev)}
        onKeyDown={(e) => {
          if (!canToggle) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsCollapsed((prev) => !prev);
          }
        }}
      >
        <div className="d-flex gap-3 align-items-center">
          <WexLogo>
            <WexText>WEX</WexText>
          </WexLogo>
          <div>
            <div className="d-flex align-items-center gap-2">
              <Title>{t("wex_fuel_card")}</Title>
              {status === STATUS.ACTIVE ? (
                <ActiveBadge>{t("active")}</ActiveBadge>
              ) : status === STATUS.CANCELLING ? (
                <CancellingBadge>{t("cancelling_sm")}</CancellingBadge>
              ) : (
                <NewBadge>{t("new_sm")}</NewBadge>
              )}
            </div>
            <Subtitle $status={status}>{subTitleText}</Subtitle>
          </div>
        </div>
        <div className="d-flex align-items-center gap-3">
          {(status === STATUS.ACTIVE || status === STATUS.CANCELLING) && (
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>
                {`${currency} ${formatNumber(addonTotal, 2)}/${billingLabel}`}
              </div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                {t("for_n_vehicles", { count: vehicleCount })}
              </div>
            </div>
          )}
          {canToggle && (
            <Chevron
              className={`fi fi-rr-angle-${isCollapsed ? "down" : "up"}`}
            />
          )}
        </div>
      </Header>

      {!isCollapsed && (
        <Body>
          {status === STATUS.CANCELLING ? (
            /* ── Cancelling state ── */
            <>
              <ReactivateAddonModal
                show={showReactivateModal}
                onHide={() => {
                  setError("");
                  setShowReactivateModal(false);
                }}
                title={t("reactivate_wex_title")}
                addonName={t("wex_fuel_card")}
                addonCost={`${currency} ${formatNumber(addonTotal, 2)}/${billingLabel}`}
                onConfirm={handleReactivate}
                loading={loading}
                error={error}
              />

              <CancellingNotice>
                <i
                  className="fi fi-rr-triangle-warning"
                  style={{
                    position: "relative",
                    top: 2,
                    marginRight: 8,
                    flexShrink: 0,
                  }}
                />
                {t("addon_cancelling_notice", {
                  addonName: t("wex_fuel_card"),
                  date: periodEndDate || "",
                })}
              </CancellingNotice>

              {/* {error && <ErrorText>{error}</ErrorText>} */}

              {isSubscriptionActive && (
                <div>
                  <LinkSpan onClick={() => setShowReactivateModal(true)}>
                    {t("reactivate_addon")}
                  </LinkSpan>
                </div>
              )}
            </>
          ) : status === STATUS.ACTIVE ? (
            /* ── Active state ── */
            <>
              {showWexPopup && (
                <Wex
                  status={isWexSetupInProgress ? 2 : 1}
                  onClose={() => setShowWexPopup(false)}
                  onFormSubmitted={async () => {
                    await refreshIntegrationsData();
                    setShowWexPopup(false);
                  }}
                />
              )}
              <CancelAddonModal
                show={showCancelConfirm}
                onHide={() => {
                  setError("");
                  setShowCancelConfirm(false);
                }}
                title={t("cancel_wex_title")}
                addonName={t("wex_fuel_card")}
                periodEndDate={periodEndDate}
                inputDuration={inputDuration}
                addonCost={`${currency} ${formatNumber(addonTotal, 2)}/${billingLabel}`}
                totalAfterCancellation={
                  totalAfterCancellation != null
                    ? `${currency} ${formatNumber(totalAfterCancellation, 2)}/${billingLabel}`
                    : null
                }
                onConfirm={handleCancel}
                loading={loading}
                error={error}
              />

              {!isWexSetupComplete && status === "active" && (
                <SetupIncompleteNotice>
                  <i
                    className="fi fi-rr-link-alt"
                    style={{ position: "relative", top: 2, flexShrink: 0 }}
                  />
                  <span>
                    <Trans
                      i18nKey={
                        isWexSetupInProgress
                          ? "wex_billing_setup_in_progress_notice"
                          : "wex_setup_incomplete"
                      }
                      t={t}
                      components={[
                        <LinkSpan
                          style={{ fontSize: "14px" }}
                          onClick={() => setShowWexPopup(true)}
                        />,
                      ]}
                    />
                  </span>
                </SetupIncompleteNotice>
              )}

              <PriceBox>
                <span style={{ fontSize: 13, color: "#6b7280" }}>
                  {`${vehicleCount} ${t("vehicles_sm")} × ${currency} ${formatNumber(unitPrice, 2)}`}
                </span>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>
                    {`${currency} ${formatNumber(addonTotal, 2)}/${billingLabel}`}
                  </div>
                  <Desc style={{ textAlign: "right" }}>
                    {t("for_n_vehicles", { count: vehicleCount })}
                  </Desc>
                </div>
              </PriceBox>

              {/* {error && <ErrorText>{error}</ErrorText>} */}

              <div>
                <LinkSpan
                  style={{ fontSize: 13, color: "#9ca3af" }}
                  onClick={() => setShowCancelConfirm(true)}
                >
                  {t("cancel_addon")}
                </LinkSpan>
              </div>
            </>
          ) : (
            /* ── Eligible state ── */
            <>
              <Section>
                <SectionTitle>{t("wex_capabilities_title")}</SectionTitle>
                <FeatureList>
                  {featureList.map((f) => (
                    <FeatureItem key={f}>
                      <i
                        className="fi fi-rs-check-circle"
                        style={{
                          color: "#16a34a",
                          position: "relative",
                          top: 2,
                        }}
                      />
                      <span>{f}</span>
                    </FeatureItem>
                  ))}
                </FeatureList>
              </Section>

              <PriceBox style={{ marginTop: "auto" }}>
                <span style={{ fontSize: 12, color: "#6b7280" }}>
                  {`${vehicleCount} ${t("vehicles_sm")} × ${currency} ${formatNumber(unitPrice, 2)}`}
                </span>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>
                    {`${currency} ${formatNumber(addonTotal, 2)}/${billingLabel}`}
                  </div>
                  <Desc style={{ textAlign: "right" }}>
                    {t("for_n_vehicles", { count: vehicleCount })}
                  </Desc>
                </div>
              </PriceBox>

              {estimatedTotal !== null && (
                <EstimatedBox>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>
                      {t("estimated_total_label")}
                    </div>
                    <Desc>{plansLabel}</Desc>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>
                      {`${currency} ${formatNumber(estimatedTotal, 2)}/${billingLabel}`}
                    </div>
                    <Desc style={{ textAlign: "right" }}>
                      {t("with_plan_selected", {
                        planName: planName || t("plan"),
                      })}
                    </Desc>
                  </div>
                </EstimatedBox>
              )}

              {/* {error && <ErrorText>{error}</ErrorText>} */}

              <Footer>
                {status === STATUS.LOCKED && onSubscribeClick ? (
                  <FilterButton
                    type="button"
                    onClick={onSubscribeClick}
                    className="w-100"
                  >
                    {t("subscribe_to_add")}
                  </FilterButton>
                ) : (
                  <AddFormButton
                    type="button"
                    // onClick={
                    //   status === STATUS.ELIGIBLE
                    //     ? () => setShowAddConfirm(true)
                    //     : undefined
                    // }
                    onClick={() => {
                      if (isMobileSubscription) {
                        setShowMobileAddOnErrPopup(true);
                        return;
                      }
                      if (!isWexEligiblePlan) {
                        setShowWexPlanErrPopup(true);
                        return;
                      }
                      setShowAddConfirm(true);
                    }}
                    disabled={status === STATUS.LOCKED}
                  >
                    {t("add_to_plan")}
                  </AddFormButton>
                )}
              </Footer>

              <ConfirmAddonModal
                show={showAddConfirm}
                onHide={() => {
                  setError("");
                  setShowAddConfirm(false);
                }}
                grandTotalLabel={grandTotalLabel}
                billingNote={billingNote}
                paymentData={paymentData}
                lineItems={[
                  ...(totalVehicleSubAmount != null
                    ? [
                        {
                          name: planName || "",
                          detail: vehicleCount
                            ? `${vehicleCount} ${t("vehicles_sm")} × ${currency} ${formatNumber(totalVehicleSubAmount / vehicleCount, 2)}`
                            : undefined,
                          amount: `${currency} ${formatNumber(totalVehicleSubAmount, 2)}/${billingLabel}`,
                        },
                      ]
                    : []),
                  {
                    name: t("wex_fuel_card"),
                    detail: `${vehicleCount} ${t("vehicles_sm")} × ${currency} ${formatNumber(unitPrice, 2)}`,
                    amount: `${currency} ${formatNumber(addonTotal, 2)}/${billingLabel}`,
                  },
                  ...(telematicsStatus === STATUS.ACTIVE && isNotOldAdvancedUser
                    ? [
                        {
                          name: t("telematics"),
                          detail: `${vehicleCount} ${t("vehicles_sm")} × ${currency} ${formatNumber(telematicsUnitPrice || 0, 2)}`,
                          amount: `${currency} ${formatNumber((telematicsUnitPrice || 0) * (vehicleCount || 0), 2)}/${billingLabel}`,
                        },
                      ]
                    : []),
                  ...(equipmentStatus === STATUS.ACTIVE
                    ? [
                        {
                          name: t("small_equipment"),
                          detail: `${equipmentQuantity} ${t("equipment_sm")} × ${currency} ${formatNumber(equipmentUnitPrice || 0, 2)}`,
                          amount: `${currency} ${formatNumber((equipmentUnitPrice || 0) * (equipmentQuantity || 0), 2)}/${billingLabel}`,
                        },
                      ]
                    : []),
                ]}
                grandTotalAmount={(() => {
                  if (totalVehicleSubAmount == null) return undefined;
                  const telematicsTotal =
                    telematicsStatus === STATUS.ACTIVE && isNotOldAdvancedUser
                      ? (telematicsUnitPrice || 0) * (vehicleCount || 0)
                      : 0;
                  const equipTotal =
                    equipmentStatus === STATUS.ACTIVE
                      ? (equipmentUnitPrice || 0) * (equipmentQuantity || 0)
                      : 0;
                  return `${currency} ${formatNumber(totalVehicleSubAmount + addonTotal + telematicsTotal + equipTotal, 2)}/${billingLabel}`;
                })()}
                onConfirm={() => {
                  //setShowAddConfirm(false);
                  handleAdd();
                }}
                loading={loading}
                error={error}
                previewParams={{
                  new_product_id: Number(currentProductId),
                  duration: inputDuration,
                  quantity: vehicleCount,
                  equipment_quantity: equipmentQuantity || 0,
                  add_fuel_card: true,
                }}
                isAddonPurchase
              />
              {/* {status === STATUS.LOCKED && (
                <LockedHelperText>
                  <i
                    className="fi fi-rr-lock"
                    style={{ position: "relative", top: 2, marginRight: 6 }}
                  />
                  {t("subscribe_to_enable_addon")}
                </LockedHelperText>
              )} */}
            </>
          )}
        </Body>
      )}
    </Wrapper>
  );
};

export default WexAddonRow;

// ── Styled components ──────────────────────────────────────────────────────

// const Wrapper = styled.div`
//   border-radius: 5px;
//   padding: 0.1rem 0.75rem;
//   box-shadow: 2px 2px 2px lightgray;
//   border: 1px solid
//     ${(props) =>
//       props.$status === STATUS.ACTIVE
//         ? "#22c55e"
//         : props.$status === STATUS.CANCELLING
//           ? "#f97316"
//           : "transparent"};
//   background: ${(props) =>
//     props.$status === STATUS.LOCKED ? "#ffffff" : "#ffffff"};
//   height: 100%;
//   display: flex;
//   flex-direction: column;
// `;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  &[role="button"] {
    cursor: pointer;
  }
`;

const Body = styled.div`
  padding: 0 12px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
`;

const WexLogo = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #f9fafb;
  border: 1px solid ${colorLightGray};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
`;

const LockedHelperText = styled.p`
  font-size: 13px;
  color: ${colorTextAdditional};
  margin: 0;
  text-align: center;
`;

const WexText = styled.span`
  font-size: 13px;
  font-weight: 900;
  color: #c41230;
  letter-spacing: -0.5px;
`;

const Title = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${(p) =>
    p.$status === STATUS.CANCELLING ? "red" : colorTextAdditional};
`;

const ActiveBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  background: #dcfce7;
  color: #166534;
  text-transform: uppercase;
`;

const NewBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  background: #dbeafe;
  color: #1d4ed8;
  text-transform: uppercase;
`;

const CancellingBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  background: #ffedd5;
  color: #c2410c;
  text-transform: uppercase;
`;

const CancellingNotice = styled.div`
  display: flex;
  align-items: flex-start;
  background: #fef9c3;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 12px 16px;
  font-size: 13.5px;
  color: #92400e;
  line-height: 1.5;
`;

const Chevron = styled.i`
  font-size: 18px;
  color: ${colorTextAdditional};
  margin-top: 4px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.h5`
  font-weight: 500;
  margin-bottom: 0;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${colorLightText};
  font-size: 13.5px;
`;

// const PriceBox = styled.div`
//   display: flex;
//   justify-content: space-between;
//   align-items: center;
//   background: #f7f7f7;
//   padding: 14px 20px;
//   border-radius: 8px;
// `;

export const EstimatedBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  padding: 14px 20px;
  border-radius: 8px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const CancelConfirm = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 12px 16px;
`;

const WarningText = styled.div`
  font-size: 13px;
  color: #991b1b;
  line-height: 1.5;
`;

const ErrorText = styled.div`
  font-size: 13px;
  color: #ef4444;
`;

const SetupIncompleteNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 14px;
  //color: #374151;
`;
